import React, { useMemo, useState } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import TagSection from './TagSection';
import LocationSection from './LocationSection';
import EvidenceSection from './EvidenceSection';
import MockupsSection from './MockupsSection';
import { useEPMContext } from '../../../../contexts/EPMContext';
import { useUserContext } from '../../../../contexts/UserContext';
import { permission } from '../../../../config/permission';
import { formatTestCardId } from '../../../../utils/converter';
import { structureOf, testId } from '../../epmHelpers';
import useAutoSave from '../useAutoSave';
import { Textarea } from '../../../UI/Input';
import useEpmValidation from '../../validation/useEpmValidation';
import { createValidator, hypothesisFormatSchema } from '../../validation/epmSchemas';
import { EPM_LIMITS } from '../../validation/epmRules';

export const SectionTitle = ({ children }) => <h3 className="stride-section-title uppercase tracking-wide text-xs md:text-sm">{children}<Info size={14} className="text-stride-muted-light" /></h3>;

const IdeaStep = ({ testData, setTestData }) => {
    const { tests } = useEPMContext();
    const { checkPermission } = useUserContext();
    const canUpdate = checkPermission(permission.XCM_TESTS_UPDATE);
    const [testsOpen, setTestsOpen] = useState(true);
    const { saveLater, flush } = useAutoSave(testData);
    const { fieldErrors, validateFieldValue } = useEpmValidation();

    const hypothesisValue = testData?.ideation?.hypothesis ?? '';
    const validateHypothesisFormat = useMemo(() => createValidator(hypothesisFormatSchema), []);

    const handleHypothesisChange = (event) => {
        if (!canUpdate) return;
        const value = event.target.value;
        validateFieldValue('hypothesis', value, validateHypothesisFormat);

        setTestData((prev) => {
            const ideation = prev?.ideation || {};
            return {
                ...prev,
                ideation: {
                    ...ideation,
                    hypothesis: value,
                },
            };
        });

        saveLater(
            'ideation.hypothesis',
            (test) => ({ ideation: { hypothesis: test?.ideation?.hypothesis ?? '' } }),
            (test) => validateHypothesisFormat(test?.ideation?.hypothesis ?? '')
        );
    };

    // Leaving the field saves what was typed without waiting for the pause
    const handleHypothesisBlur = () => {
        if (!canUpdate) return;
        flush('ideation.hypothesis');
    };

    // Concluded tests sharing the most tags with this one (01 §3.6)
    const similarTests = useMemo(() => {
        const tagIds = new Set((testData?.tag_ids || []).map(String));
        if (!tagIds.size) return [];
        return (tests || [])
            .filter((test) => test.status === 'CONCLUDED' && String(testId(test)) !== String(testId(testData)))
            .map((test) => ({ test, overlap: (test.tag_ids || []).filter((id) => tagIds.has(String(id))).length }))
            .filter(({ overlap }) => overlap > 0)
            .sort((a, b) => b.overlap - a.overlap)
            .slice(0, 3)
            .map(({ test }) => test);
    }, [tests, testData]);

    const verdicts = similarTests.map((test) => structureOf(test).result?.verdict).filter(Boolean);
    const wins = verdicts.filter((verdict) => verdict === 'win').length;
    const verdictSummary = ['win', 'loss', 'inconclusive']
        .map((verdict) => [verdicts.filter((v) => v === verdict).length, verdict])
        .filter(([count]) => count)
        .map(([count, verdict]) => `${count} ${verdict}`)
        .join(', ');

    return (
        <div className="epm-idea">
            <header className="epm-workflow-step__heading epm-idea__heading">
                <span>01</span>
                <div>
                    <h2>Idea</h2>
                    <p>Capture the opportunity, proposed change, and evidence behind the test.</p>
                </div>
            </header>

            <section className="epm-idea__card epm-idea__hypothesis">
                <SectionTitle>Hypothesis</SectionTitle>
                <div className="w-full">
                    <Textarea
                        id="epm-field-hypothesis"
                        disabled={!canUpdate}
                        value={hypothesisValue}
                        onChange={handleHypothesisChange}
                        onBlur={handleHypothesisBlur}
                        placeholder="e.g. Because the mobile PDP funnel drops 31% on scroll, we believe a sticky basket button at mobile PDP buybox will cause add-to-basket rate to rise by 3% for mobile visitors."
                        rows={3}
                        maxLength={EPM_LIMITS.HYPOTHESIS_MAX}
                        error={fieldErrors.hypothesis}
                        showCharCount
                        aria-label="hypothesis"
                    />
                </div>
                {testData?.description && (
                    <p className="epm-idea__note">Original note: "{testData.description}"</p>
                )}
            </section>

            <LocationSection testData={testData} setTestData={setTestData} />

            <EvidenceSection testData={testData} setTestData={setTestData} />

            <TagSection testData={testData} setTestData={setTestData} />

            <MockupsSection testData={testData} setTestData={setTestData} />

            <section className="epm-idea__card">
                <button
                    className="epm-idea__disclosure"
                    type="button"
                    onClick={() => setTestsOpen(!testsOpen)}
                    aria-expanded={testsOpen}
                >
                    <ChevronDown className={testsOpen ? '' : '-rotate-90'} size={17} />
                    <strong>Similar past tests</strong>
                    <span>
                        {similarTests.length
                            ? `${similarTests.length} found${verdictSummary ? ` · ${verdictSummary}` : ''}`
                            : 'None yet · add tags to find them'}
                    </span>
                    <em>{testsOpen ? 'Hide' : 'Show'}</em>
                </button>
                {testsOpen && similarTests.length > 0 && (
                    <div className="epm-idea__past-tests">
                        {similarTests.map((test) => {
                            const result = structureOf(test).result || {};
                            return (
                                <article key={String(testId(test))}>
                                    <div>
                                        <p><strong>{formatTestCardId(test)}</strong> {test.name}</p>
                                        <small>{result.detail}</small>
                                    </div>
                                    {result.verdict && <span className={`is-${result.verdict}`}>{result.verdict}</span>}
                                </article>
                            );
                        })}
                        {wins > 0 && (
                            <p className="epm-idea__note">
                                A related win raises the suggested Confidence at prioritisation. It can be overridden there with a note.
                            </p>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
};

export default IdeaStep;
